import {
  filasDeEtiquetas,
  formatoPrecio,
  pisoEnTexto,
  techoEnTexto,
} from '../components/barra-de-rangos';
import {
  cualesCumplir,
  DISTANCIA_ENTRE_ROTULOS,
  esDeTramo,
  REFERENCIA,
  VERBOS,
  type RecorridoDelPrecio,
  type ReglaDelRecorrido,
  type Tiempo,
} from './recorrido-del-precio';

/**
 * El dibujo que se explica solo (spec 227 §6; maqueta v2, la función
 * `dibujo()`): marcas con nombre y valor, lo verde, la zona a pérdida debajo
 * del costo, la flecha con punta de lo que movieron las reglas y, sin precio
 * posible, el hueco en rojo con dos carriles. Una leyenda con sólo lo que
 * aparece. Reemplaza a `ejeDelRecorrido` y sus barras finas.
 */

const CASI = 0.005;
const fmt = formatoPrecio;

export type ClaveDeMarca =
  | 'pide'
  | 'hoy'
  | 'queda'
  | 'costo'
  | 'masBarato'
  | 'conReglasDeHoy'
  | 'piso'
  | 'techo'
  | 'minNueva'
  | 'maxNueva'
  // Lote B1 (pieza 2): un rombo con nombre por tienda que cuenta.
  | `tienda-${number}`;

/** Un rombo del mercado: el más barato de siempre, o una tienda con su nombre (pieza 2). */
export const esDelMercado = (c: ClaveDeMarca): boolean =>
  c === 'masBarato' || c.startsWith('tienda-');

export interface MarcaDelDibujo {
  clave: ClaveDeMarca;
  /** «pedía la estrategia», «tu costo», «el más barato»… */
  nombre: string;
  valor: number;
  lado: 'ARRIBA' | 'ABAJO';
  /** 0 es la fila pegada a la línea; los rótulos cercanos suben o bajan una. */
  fila: number;
  /**
   * Cerca de los bordes el rótulo no se centra en su marca: se alinea hacia
   * adentro para no salirse de una tarjeta angosta (auditoría I4).
   */
  alinea: 'IZQUIERDA' | 'CENTRO' | 'DERECHA';
}

/** Menos de esto del borde (en fracción del eje), el rótulo se alinea hacia adentro. */
export const BORDE_DEL_ROTULO = 0.12;

export type ClaveDeLeyenda =
  | 'hueco'
  | 'verde'
  | 'perdida'
  | 'carrilPiso'
  | 'carrilTecho'
  | 'masBarato'
  | 'flecha';

export interface DibujoDelPrecio {
  desde: number;
  hasta: number;
  /** Donde se cumplen todas. max null = hasta el borde. null sin tramos o sin precio posible. */
  verde: { min: number | null; max: number | null } | null;
  /** Hasta dónde llega la zona «a pérdida»: el costo. null sin costo. */
  perdida: number | null;
  /** Sin precio posible: entre el techo (`de`) y el piso (`a`) no hay precio. */
  hueco: { de: number; a: number } | null;
  /**
   * Con el hueco: «ningún precio cumple las dos» o «… todas», según cuántas
   * reglas ponen límite (informe 229, textos). null sin hueco.
   */
  cualesCumplir: 'las dos' | 'todas' | null;
  /** Sin precio posible: lo que permite cada lado, en su carril. */
  carriles: Array<{
    clave: 'piso' | 'techo';
    titulo: string;
    min: number | null;
    max: number | null;
  }>;
  /** De lo que pedía la estrategia a donde queda. null si las reglas no lo movieron. */
  flecha: { de: number; a: number } | null;
  marcas: MarcaDelDibujo[];
  leyenda: Array<{ clave: ClaveDeLeyenda; texto: string }>;
  /** Lo mismo en palabras, para el lector de pantalla. */
  enPalabras: string;
}

/** «tu margen», «la regla nueva», ««No quedar más de 20 %…»». */
function quien(regla: ReglaDelRecorrido | undefined): string {
  if (!regla) return 'una regla';
  if (regla.probando) return 'la regla nueva';
  if (regla.duro && regla.tipo === 'MARGEN_MINIMO') return 'tu margen';
  return `«${regla.ruleName}»`;
}
const conMayuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** El dibujo. null cuando no hay qué dibujar (sin cálculo, o sin los rangos guardados). */
export function dibujoDelPrecio(r: RecorridoDelPrecio, tiempo: Tiempo): DibujoDelPrecio | null {
  if (r.estado === 'SIN_CALCULO' || !r.reglas || !r.cruce) return null;
  const tramos = r.reglas.filter(esDeTramo);
  const sinPrecio = r.estado === 'SIN_PRECIO_POSIBLE';
  const pide = r.pedido && r.pedido.origen !== 'HOY' ? r.pedido.precio : null;
  // Sin precio posible y ya resuelta: donde quedó es la salida elegida.
  const queda = r.queda ?? r.elegida?.precio ?? null;
  const c = r.comparacion;
  const igual = c != null && c.sin != null && c.con != null && Math.abs(c.con - c.sin) < CASI;
  // Lote B1 (pieza 2): con las tiendas congeladas, un rombo con nombre por cada
  // una en lugar del «más barato» sin nombre. El mercado de HOY (una propuesta
  // de antes) no se mezcla con la cuenta congelada: el rombo de siempre
  // (auditoría del 3-oct).
  const tiendas =
    r.mercado && !r.mercado.deHoy ? [...r.mercado.tiendas].sort((a, b) => a.precio - b.precio) : [];
  const mb = tiendas.length > 0 ? tiendas[0].precio : r.masBarato;
  const pideEsElMasBarato = pide != null && mb != null && Math.abs(pide - mb) < CASI;

  const marcas: Array<Omit<MarcaDelDibujo, 'fila' | 'alinea'>> = [];
  if (pide != null)
    marcas.push({
      clave: 'pide',
      nombre: pideEsElMasBarato
        ? `pedía la estrategia = ${tiendas.length > 0 ? tiendas[0].nombre : 'el más barato'}`
        : 'pedía la estrategia',
      valor: pide,
      lado: 'ARRIBA',
    });
  if (r.hoy != null)
    marcas.push({ clave: 'hoy', nombre: REFERENCIA[tiempo], valor: r.hoy, lado: 'ARRIBA' });
  if (queda != null)
    marcas.push({
      clave: 'queda',
      nombre: c ? (igual ? 'queda' : 'quedaría') : VERBOS[tiempo].toLowerCase(),
      valor: queda,
      lado: 'ARRIBA',
    });
  if (r.costo != null)
    marcas.push({ clave: 'costo', nombre: 'tu costo', valor: r.costo, lado: 'ABAJO' });
  if (tiendas.length > 0) {
    // Abajo y arriba, alternadas: dos tiendas cerca no se pisan el nombre.
    tiendas.forEach((t, i) => {
      if (i === 0 && pideEsElMasBarato) return;
      marcas.push({
        clave: `tienda-${i}`,
        nombre: t.nombre,
        valor: t.precio,
        lado: i % 2 === 0 ? 'ABAJO' : 'ARRIBA',
      });
    });
  } else if (mb != null && !pideEsElMasBarato)
    marcas.push({ clave: 'masBarato', nombre: 'el más barato', valor: mb, lado: 'ABAJO' });
  if (c && c.sin != null && queda != null && !igual && !sinPrecio)
    marcas.push({
      clave: 'conReglasDeHoy',
      nombre: 'con tus reglas de hoy',
      valor: c.sin,
      lado: 'ABAJO',
    });

  // «¿Y si…?» que no lo cambia: dónde empieza y dónde termina lo que deja la regla nueva.
  if (c && igual) {
    const nueva = tramos.filter((x) => x.probando);
    const mins = nueva.filter((x) => x.min != null).map((x) => x.min!);
    const maxs = nueva.filter((x) => x.max != null).map((x) => x.max!);
    if (mins.length)
      marcas.push({
        clave: 'minNueva',
        nombre: 'lo mínimo que deja la regla nueva',
        valor: Math.max(...mins),
        lado: 'ABAJO',
      });
    if (maxs.length)
      marcas.push({
        clave: 'maxNueva',
        nombre: mins.length ? 'lo máximo que deja' : 'lo máximo que deja la regla nueva',
        valor: Math.min(...maxs),
        lado: 'ABAJO',
      });
  }

  let hueco: DibujoDelPrecio['hueco'] = null;
  let carriles: DibujoDelPrecio['carriles'] = [];
  let piso: ReglaDelRecorrido | undefined;
  let techo: ReglaDelRecorrido | undefined;
  if (sinPrecio && r.cruce.vacio && r.cruce.min != null && r.cruce.max != null) {
    const { min, max } = r.cruce;
    piso = tramos.find((x) => x.min != null && Math.abs(x.min - min) < CASI);
    techo = tramos.find((x) => x.max != null && Math.abs(x.max - max) < CASI);
    marcas.push(
      { clave: 'techo', nombre: `techo de ${quien(techo)}`, valor: max, lado: 'ARRIBA' },
      { clave: 'piso', nombre: `piso de ${quien(piso)}`, valor: min, lado: 'ARRIBA' }
    );
    hueco = { de: max, a: min };
    carriles = [
      {
        clave: 'piso',
        titulo: `${conMayuscula(quien(piso))} permite: desde ${pisoEnTexto(min)}`,
        min,
        max: null,
      },
      {
        clave: 'techo',
        titulo: `${conMayuscula(quien(techo))} permite: hasta ${techoEnTexto(max)}`,
        min: null,
        max,
      },
    ];
  }

  const verde =
    !sinPrecio && !r.cruce.vacio && tramos.length > 0
      ? { min: r.cruce.min, max: r.cruce.max }
      : null;
  // La flecha es lo que movieron las REGLAS: si sólo redondeó, no hay flecha.
  const despues = r.redondeo ? r.redondeo.desde : queda;
  const flecha =
    !sinPrecio &&
    pide != null &&
    queda != null &&
    despues != null &&
    Math.abs(despues - pide) >= CASI
      ? { de: pide, a: queda }
      : null;

  const puntos = [
    ...marcas.map((m) => m.valor),
    ...(verde ? [verde.min, verde.max] : []),
    ...(hueco ? [hueco.de, hueco.a] : []),
  ].filter((v): v is number => v != null);
  if (puntos.length === 0) puntos.push(0, 1); // defensivo: sin ningún precio que marcar
  const lo = Math.min(...puntos);
  const hi = Math.max(...puntos);
  // Aire a los costados: 8 % del tramo, o 10 % del valor si todo es un punto (como `ejeDe`).
  const aire = hi > lo ? (hi - lo) * 0.08 : Math.max(Math.abs(lo) * 0.1, 1);
  const desde = Math.max(0, lo - aire);
  const hasta = hi + aire;
  const pos = (v: number) => (v - desde) / (hasta - desde);
  // Pulido (informe 229, maqueta del historial): si «pedía la estrategia» se pega
  // a «queda», el que sobra pasa al lado con menos rótulos (contados sin él; con
  // empate, abajo). En el Colchón Templanza baja y deja libre a «queda». La
  // misma baja contra «hoy» (ronda final, A1): el nombre es más largo que los
  // demás rótulos, así que su distancia mínima crece con su longitud (el umbral
  // base calibra rótulos de ~16 caracteres en un eje típico).
  const marcaPide = marcas.find((m) => m.clave === 'pide');
  if (marcaPide) {
    // OJO: este umbral escalado sólo decide de qué LADO va «pedía»; las filas de cada
    // lado (`filasDeEtiquetas` más abajo) siguen usando la distancia base, que sirve
    // para todas las marcas por igual. Una vez que «pedía» pasó al otro lado, su nombre
    // largo todavía puede rozar a una marca vecina de ese lado; hoy el caso del JBL
    // lo fija el test «pedía baja cuando se pega a hoy». Si aparece otro, la salida es
    // una distancia por marca en `filasDeEtiquetas`, no tocar este umbral.
    const umbralConHoy = DISTANCIA_ENTRE_ROTULOS * (marcaPide.nombre.length / 16);
    const pisaArriba = marcas.some(
      (m) =>
        m !== marcaPide &&
        m.lado === 'ARRIBA' &&
        ((m.clave === 'queda' &&
          Math.abs(pos(m.valor) - pos(marcaPide.valor)) < DISTANCIA_ENTRE_ROTULOS) ||
          (m.clave === 'hoy' && Math.abs(pos(m.valor) - pos(marcaPide.valor)) < umbralConHoy))
    );
    if (pisaArriba) {
      const cuantos = (lado: 'ARRIBA' | 'ABAJO') =>
        marcas.filter((m) => m !== marcaPide && m.lado === lado).length;
      marcaPide.lado = cuantos('ABAJO') <= cuantos('ARRIBA') ? 'ABAJO' : 'ARRIBA';
    }
  }
  const filas = (lado: 'ARRIBA' | 'ABAJO') =>
    filasDeEtiquetas(
      marcas.filter((m) => m.lado === lado).map((m) => ({ clave: m.clave, pos: pos(m.valor) })),
      DISTANCIA_ENTRE_ROTULOS
    );
  const filaArriba = filas('ARRIBA');
  const filaAbajo = filas('ABAJO');

  const leyenda: DibujoDelPrecio['leyenda'] = [];
  const cuales = hueco ? cualesCumplir(r.reglas) : null;
  if (hueco) leyenda.push({ clave: 'hueco', texto: `el hueco: ningún precio cumple ${cuales}` });
  if (verde) leyenda.push({ clave: 'verde', texto: 'precios que cumplen tus reglas' });
  if (r.costo != null) leyenda.push({ clave: 'perdida', texto: 'debajo de tu costo: a pérdida' });
  if (hueco) {
    leyenda.push({ clave: 'carrilPiso', texto: `lo que permite ${quien(piso)}` });
    leyenda.push({ clave: 'carrilTecho', texto: `lo que permite ${quien(techo)}` });
  }
  if (marcas.some((m) => m.clave === 'masBarato'))
    leyenda.push({ clave: 'masBarato', texto: 'el más barato del mercado' });
  else if (tiendas.length > 0) leyenda.push({ clave: 'masBarato', texto: 'una tienda que cuenta' });
  if (flecha) leyenda.push({ clave: 'flecha', texto: 'lo que movieron tus reglas' });

  const enPalabras = [
    ...[...marcas].sort((a, b) => a.valor - b.valor).map((m) => `${m.nombre} ${fmt(m.valor)}`),
    verde
      ? `verde ${[verde.min != null ? `desde ${fmt(verde.min)}` : '', verde.max != null ? `hasta ${fmt(verde.max)}` : ''].filter(Boolean).join(' ')}`
      : hueco
        ? `ningún precio entre ${fmt(hueco.de)} y ${fmt(hueco.a)}`
        : 'sin verde',
  ].join(', ');

  return {
    desde,
    hasta,
    verde,
    perdida: r.costo,
    hueco,
    cualesCumplir: cuales,
    carriles,
    flecha,
    marcas: marcas.map((m) => ({
      ...m,
      fila: (m.lado === 'ARRIBA' ? filaArriba : filaAbajo)[m.clave],
      alinea:
        pos(m.valor) < BORDE_DEL_ROTULO
          ? ('IZQUIERDA' as const)
          : pos(m.valor) > 1 - BORDE_DEL_ROTULO
            ? ('DERECHA' as const)
            : ('CENTRO' as const),
    })),
    leyenda,
    enPalabras,
  };
}
